import { useEffect, useMemo, useReducer, useState } from "react"
import { ActionIcon, Button, Flex, Group, Stack, Text, TextInput, Tooltip } from "@mantine/core"
import { DataTable, DataTableSortStatus } from "mantine-datatable"
import { IconEdit, IconPlus, IconSearch, IconTrash, IconX } from "@tabler/icons-react"
import { useDeleteWorkshop, useWorkshops } from "../hooks/use-workshops"
import { MSaveWorkshop } from "../components/MSaveWorkshop"
import { PAGE_SIZES, ROLES } from "@/shared"
import { useAuth } from "@features/auth/hooks/useAuth.ts"
import { headerStore } from "@shared/state/header.state.ts"
import { openConfirmModal } from "@mantine/modals"
import { useDynamicHeight } from "@/shared/hooks/responsive.queries"
import dayjs from "dayjs"
import _ from "lodash"

interface SearchWorkshops {
  tema: string
  expositor: string
}

interface WorkshopsState {
  search: SearchWorkshops
  sortStatus: DataTableSortStatus
  page: number
  pageSize: number
}

type WorkshopsAction = { type: "SET_SEARCH"; payload: Partial<SearchWorkshops> } | { type: "SET_SORT_STATUS"; payload: DataTableSortStatus } | { type: "SET_PAGE"; payload: number } | { type: "SET_PAGE_SIZE"; payload: number }

const initialWorkshopsState: WorkshopsState = {
  search: { tema: "", expositor: "" },
  sortStatus: { columnAccessor: "fecha", direction: "desc" },
  page: 1,
  pageSize: PAGE_SIZES[1]
}

function workshopsReducer(state: WorkshopsState, action: WorkshopsAction): WorkshopsState {
  switch (action.type) {
    case "SET_SEARCH":
      return { ...state, search: { ...state.search, ...action.payload }, page: 1 }
    case "SET_SORT_STATUS":
      return { ...state, sortStatus: action.payload }
    case "SET_PAGE":
      return { ...state, page: action.payload }
    case "SET_PAGE_SIZE":
      return { ...state, pageSize: action.payload, page: 1 }
    default:
      return state
  }
}

export default function WorkshopsPage() {
  const { hasRole } = useAuth()
  const { tableHeight } = useDynamicHeight()
  const [state, dispatch] = useReducer(workshopsReducer, initialWorkshopsState)
  const { data: workshops = [], isFetching, refetch } = useWorkshops()
  const deleteWorkshopMutation = useDeleteWorkshop()

  const [openedWorkshop, setOpenedWorkshop] = useState(false)
  const [editingWorkshop, setEditingWorkshop] = useState<any | null>(null)

  useEffect(() => {
    headerStore.setTitle("Talleres")
    headerStore.setOnRefresh(() => refetch())
    return () => {
      headerStore.setTitle(null)
      headerStore.setOnRefresh(null)
    }
  }, [refetch])

  const filteredWorkshops = useMemo(() => {
    let result = [...workshops]
    if (state.search.tema) {
      result = result.filter((w) => w.tema?.toLowerCase().includes(state.search.tema.toLowerCase()))
    }
    if (state.search.expositor) {
      result = result.filter((w) => w.expositor?.toLowerCase().includes(state.search.expositor.toLowerCase()))
    }
    result = _.orderBy(result, [state.sortStatus.columnAccessor], [state.sortStatus.direction])
    return result
  }, [workshops, state.search, state.sortStatus])

  const paginatedWorkshops = useMemo(() => {
    const from = (state.page - 1) * state.pageSize
    const to = from + state.pageSize
    return filteredWorkshops.slice(from, to)
  }, [filteredWorkshops, state.page, state.pageSize])

  const handleCreateWorkshop = () => {
    setEditingWorkshop(null)
    setOpenedWorkshop(true)
  }

  const handleEditWorkshop = (workshop: any) => {
    setEditingWorkshop(workshop)
    setOpenedWorkshop(true)
  }

  const handleDeleteWorkshop = (id: string) => {
    openConfirmModal({
      title: "Eliminar Taller",
      centered: true,
      children: <Text size="sm">¿Está seguro que desea eliminar este taller? Esta acción no se puede deshacer.</Text>,
      labels: { confirm: "Eliminar", cancel: "Cancelar" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteWorkshopMutation.mutate(id)
    })
  }

  return (
    <Stack gap="md">
      <Flex justify="flex-end">
        {(hasRole(ROLES.ADMIN) || hasRole(ROLES.EDUCATION_CREATE)) && (
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreateWorkshop}>
            Agregar Taller
          </Button>
        )}
      </Flex>

      <DataTable
        withTableBorder
        borderRadius="md"
        striped
        highlightOnHover
        fetching={isFetching}
        records={paginatedWorkshops}
        totalRecords={filteredWorkshops.length}
        recordsPerPage={state.pageSize}
        page={state.page}
        onPageChange={(p) => dispatch({ type: "SET_PAGE", payload: p })}
        recordsPerPageOptions={PAGE_SIZES}
        onRecordsPerPageChange={(s) => dispatch({ type: "SET_PAGE_SIZE", payload: s })}
        recordsPerPageLabel="Cantidad por página"
        sortStatus={state.sortStatus}
        onSortStatusChange={(s) => dispatch({ type: "SET_SORT_STATUS", payload: s })}
        columns={[
          {
            accessor: "tema",
            title: "Tema",
            sortable: true,
            filtering: state.search.tema !== "",
            filter: (
              <TextInput
                placeholder="Buscar tema"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.tema && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => dispatch({ type: "SET_SEARCH", payload: { tema: "" } })}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.tema}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { tema: e.currentTarget.value } })}
              />
            )
          },
          { accessor: "fecha", title: "Fecha", sortable: true, render: (r: any) => (r.fecha ? dayjs(r.fecha).format("DD/MM/YYYY") : "-") },
          {
            accessor: "expositor",
            title: "Expositor",
            sortable: true,
            filtering: state.search.expositor !== "",
            filter: (
              <TextInput
                placeholder="Buscar expositor"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.expositor && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => dispatch({ type: "SET_SEARCH", payload: { expositor: "" } })}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.expositor}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { expositor: e.currentTarget.value } })}
              />
            )
          },
          { accessor: "participantes", title: "Participantes" },
          {
            accessor: "actions",
            title: "Acciones",
            textAlign: "right",
            hidden: !hasRole(ROLES.ADMIN) && !hasRole(ROLES.EDUCATION_EDIT) && !hasRole(ROLES.EDUCATION_DELETE),
            render: (workshop: any) => (
              <Group gap={4} justify="right" wrap="nowrap">
                {(hasRole(ROLES.ADMIN) || hasRole(ROLES.EDUCATION_EDIT)) && (
                  <Tooltip label="Editar Taller">
                    <ActionIcon size="sm" variant="subtle" color="blue.7" onClick={() => handleEditWorkshop(workshop)}>
                      <IconEdit size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {(hasRole(ROLES.ADMIN) || hasRole(ROLES.EDUCATION_DELETE)) && (
                  <Tooltip label="Eliminar Taller">
                    <ActionIcon size="sm" variant="subtle" color="red.7" onClick={() => handleDeleteWorkshop(workshop.id)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            )
          }
        ]}
        style={{ height: tableHeight }}
      />

      <MSaveWorkshop opened={openedWorkshop} onClose={() => setOpenedWorkshop(false)} editingWorkshop={editingWorkshop} />
    </Stack>
  )
}
